import React, { useState } from 'react';
import styles from './Login.module.css';
import { Button } from '../../components/common/Button/Button';
import { authAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const omgevingsCompasLogo = '/assets/logos/OmgevingsCompas_logo.svg';

interface LoginProps {
  onLogin: (email: string, password: string, remember: boolean) => void;
  onLinkedInLogin?: () => void;
}

// De 5 fasen van de Leene-methode — getoond als flow op het marketingpaneel.
const FLOW_STEPS: { title: string; desc: string; icon: React.ReactNode }[] = [
  {
    title: 'Projectverkenning',
    desc: 'Breng de opgave, context en doelstellingen helder in kaart.',
    icon: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>,
  },
  {
    title: 'Omgevingsanalyse',
    desc: 'Krijg inzicht in trends, ontwikkelingen en belanghebbenden.',
    icon: <><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
  },
  {
    title: "Persona's",
    desc: 'Begrijp de drijfveren, behoeften en zorgen van uw doelgroepen.',
    icon: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
  },
  {
    title: 'Communicatiestrategie',
    desc: 'Bepaal de juiste aanpak en middelen voor maximale impact.',
    icon: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  },
  {
    title: 'Kernboodschap',
    desc: 'Formuleer een krachtige en consistente boodschap die verbindt.',
    icon: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" fill="currentColor" /></>,
  },
];

export const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const toast = useToast();
  const [email, setEmail] = useState(() => localStorage.getItem('rememberedEmail') || '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (rememberMe) localStorage.setItem('rememberedEmail', email);
    else localStorage.removeItem('rememberedEmail');
    onLogin(email, password, rememberMe);
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      await authAPI.forgotPassword(forgotEmail);
      setForgotSent(true);
    } catch (err: any) {
      toast.error('Verzenden mislukt', err?.response?.data?.error || 'Probeer het later opnieuw.');
    } finally {
      setForgotBusy(false);
    }
  };

  const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' };

  return (
    <div className={styles.loginScreen}>
      {/* Left side - Login form */}
      <div className={styles.loginLeft}>
        <div className={styles.loginBox}>
          <img src={omgevingsCompasLogo} alt="OmgevingsCompas" className={styles.loginLogo} />

          {mode === 'login' ? (
            <>
              <p className={styles.loginSubtitle}>Log in om door te gaan naar uw projectomgeving</p>

              <form className={styles.loginForm} onSubmit={handleSubmit}>
                <div className={styles.inputGroup}>
                  <label className={styles.inputLabel}>E-mailadres</label>
                  <input
                    type="text"
                    className={styles.inputField}
                    placeholder="naam@organisatie.nl"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label className={styles.inputLabel}>Wachtwoord</label>
                  <input
                    type="password"
                    className={styles.inputField}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <div className={styles.formOptions}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className={styles.checkbox}
                    />
                    Onthoud mij
                  </label>
                  <button
                    type="button"
                    className={styles.forgotLink}
                    style={linkBtn}
                    onClick={() => { setForgotEmail(email); setForgotSent(false); setMode('forgot'); }}
                  >
                    Wachtwoord vergeten?
                  </button>
                </div>

                <Button type="submit" variant="primary" size="large" fullWidth>
                  Inloggen
                </Button>
              </form>
            </>
          ) : (
            <>
              <p className={styles.loginSubtitle}>
                Vul je e-mailadres in; je ontvangt een link om je wachtwoord opnieuw in te stellen.
              </p>

              {forgotSent ? (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '0.95rem', color: '#323232', lineHeight: 1.6, marginBottom: '1.5rem' }}>
                    Als dit e-mailadres bij ons bekend is, ontvang je een e-mail met instructies om je wachtwoord opnieuw in te stellen.
                  </p>
                  <button type="button" className={styles.forgotLink} style={linkBtn} onClick={() => { setMode('login'); setForgotSent(false); }}>
                    ← Terug naar inloggen
                  </button>
                </div>
              ) : (
                <form className={styles.loginForm} onSubmit={handleForgot}>
                  <div className={styles.inputGroup}>
                    <label className={styles.inputLabel}>E-mailadres</label>
                    <input
                      type="email"
                      className={styles.inputField}
                      placeholder="naam@organisatie.nl"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                    />
                  </div>

                  <Button type="submit" variant="primary" size="large" fullWidth disabled={forgotBusy}>
                    {forgotBusy ? 'Versturen…' : 'Stuur resetlink'}
                  </Button>

                  <p className={styles.contactText}>
                    <button type="button" className={styles.forgotLink} style={linkBtn} onClick={() => setMode('login')}>
                      ← Terug naar inloggen
                    </button>
                  </p>
                </form>
              )}
            </>
          )}
        </div>
      </div>

      {/* Right side - Marketing panel met achtergrondfoto + fase-flow */}
      <div
        className={styles.loginRight}
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/header.jpg)` }}
      >
        <div className={styles.rightOverlay} />
        <div className={styles.rightContent}>
          <img
            src={`${process.env.PUBLIC_URL}/assets/logos/logo_leene_wit.svg`}
            alt="Leene Communicatie"
            className={styles.rightLogo}
          />
          <h1 className={styles.rightTitle}>
            De Leene-methode voor omgevingscommunicatie
          </h1>

          <div className={styles.flowSteps}>
            {FLOW_STEPS.map((step, i) => {
              const active = i === 0;
              const last = i === FLOW_STEPS.length - 1;
              return (
                <div className={styles.flowStep} key={step.title}>
                  <div className={styles.flowMarker}>
                    <div className={`${styles.flowCircle} ${active ? styles.flowCircleActive : ''}`}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        {step.icon}
                      </svg>
                      <span className={styles.flowNum}>{i + 1}</span>
                    </div>
                    {!last && <span className={styles.flowLine} />}
                  </div>
                  <div className={styles.flowText}>
                    <h3 className={styles.flowTitle}>{step.title}</h3>
                    <p className={styles.flowDesc}>{step.desc}</p>
                    {active && <span className={styles.flowBadge}>Huidige fase</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
